import { Helmet } from "react-helmet";
import { OG_IMAGE, SITE_NAME, SITE_URL } from "./site";

type SeoProps = {
  /** Full page title, ~50–60 characters, main keyword first. */
  title: string;
  /** ~140–160 characters; shown under the link in Google. */
  description: string;
  /** Route path, e.g. "/" or "/services". Used for the canonical URL. */
  path: string;
  /** Keep placeholder pages out of Google until they have real content. */
  noindex?: boolean;
  /** JSON-LD objects for this page. */
  schema?: object[];
};

/**
 * Per-page SEO tags. Every page in src/pages renders one of these.
 * Defaults for crawlers that don't run JavaScript live in public/index.html.
 */
const Seo = ({ title, description, path, noindex, schema = [] }: SeoProps) => {
  const url = `${SITE_URL}${path === "/" ? "/" : path}`;
  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large"} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_CA" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Infinitude Stack — software studio in Kitchener, Ontario" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {schema.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
